import { cn } from "../lib/utils";
import { IconMenu2 } from "@tabler/icons-react";
import { AnimatePresence, MotionValue, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import React from "react";
import { useRef, useState } from "react";
// import { a } from "react-router-dom";
type ItemType = { title: string; icon: React.ReactNode; onClick: () => void };
export const FloatingDock = ({
	items,
	desktopClassName,
	mobileClassName,
}: {
	items: ItemType[];
	desktopClassName?: string;
	mobileClassName?: string;
}) => {
	return (
		<>
			<FloatingDockDesktop items={items} className={desktopClassName} />
			<FloatingDockMobile items={items} className={mobileClassName} />
		</>
	);
};

const FloatingDockMobile = ({ items, className }: { items: ItemType[]; className?: string }) => {
	const [open, setOpen] = useState(false);
	return (
		<div className={cn("relative block md:hidden z-50", className)}>
			<AnimatePresence>
				{open && (
					<motion.div layoutId='nav' className='absolute bottom-full mb-2 inset-x-0 flex flex-col gap-2'>
						{items.map((item, idx) => (
							<motion.div
								onClick={item.onClick}
								key={item.title}
								initial={{ opacity: 0, y: 10 }}
								animate={{
									opacity: 1,
									y: 0,
								}}
								exit={{
									opacity: 0,
									y: 10,
									transition: {
										delay: idx * 0.05,
									},
								}}
								transition={{ delay: (items.length - 1 - idx) * 0.05 }}>
								<span key={item.title} className='h-12 w-12 rounded-full bg-black flex items-center justify-center'>
									{item.icon}
								</span>
							</motion.div>
						))}
					</motion.div>
				)}
			</AnimatePresence>
			<button
				onClick={() => setOpen(!open)}
				className='h-12 w-12 rounded-full bg-black flex items-center justify-center'>
				<IconMenu2 className='text-neutral-100 ' />
			</button>
		</div>
	);
};

const FloatingDockDesktop = ({ items, className }: { items: ItemType[]; className?: string }) => {
	let mouseX = useMotionValue(Infinity);
	return (
		<motion.div
			onMouseMove={(e) => mouseX.set(e.pageX)}
			onMouseLeave={() => mouseX.set(Infinity)}
			className={cn(
				"mx-auto hidden md:flex h-16 gap-4 items-end rounded-2xl blurred drop-shadow-lg px-4 pb-3 z-20",
				className
			)}>
			{items.map((item) => (
				<IconContainer mouseX={mouseX} key={item.title} {...item} />
			))}
		</motion.div>
	);
};

function IconContainer({
	mouseX,
	title,
	icon,
	onClick,
}: {
	mouseX: MotionValue;
	title: string;
	icon: React.ReactNode;
	onClick: () => void;
}) {
	let ref = useRef<HTMLDivElement>(null);

	let distance = useTransform(mouseX, (val) => {
		let bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };

		return val - bounds.x - bounds.width / 2;
	});

	let widthTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);
	let heightTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);

	let widthTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);
	let heightTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);

	let width = useSpring(widthTransform, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	});
	let height = useSpring(heightTransform, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	});

	let widthIcon = useSpring(widthTransformIcon, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	});
	let heightIcon = useSpring(heightTransformIcon, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	});

	const [hovered, setHovered] = useState(false);

	return (
		<motion.div
			ref={ref}
			onClick={onClick}
			style={{ width, height }}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className='aspect-square rounded-full bg-black flex items-center justify-center relative cursor-pointer'>
			<AnimatePresence>
				{hovered && (
					<motion.div
						initial={{ opacity: 0, y: 10, x: "-50%" }}
						animate={{ opacity: 1, y: 0, x: "-50%" }}
						exit={{ opacity: 0, y: 2, x: "-50%" }}
						className='px-2 py-0.5 whitespace-pre rounded-md border bg-black border-neutral-900 text-white absolute left-1/2 -translate-x-1/2 -top-8 w-fit text-base'>
						{title}
					</motion.div>
				)}
			</AnimatePresence>
			<motion.div
				style={{ width: widthIcon, height: heightIcon }}
				initial={{ scale: 1 }}
				animate={{ scale: hovered ? 2 : 1 }}
				exit={{ scale: 1 }}
				className='flex items-center justify-center'>
				{icon}
			</motion.div>
		</motion.div>
	);
}
